import {
  errorMessage,
  useImportPreview,
  useStartImportPreview,
} from "@via/pages/projects/metadata/configuration/data-import/data-import.api.tsx";
import type { VmetaImportDryRunResult } from "common";
import { FC, useState } from "react";

export const PREVIEW_LINES: {
  label: string;
  meaning: string;
  value: (r: VmetaImportDryRunResult) => number;
}[] = [
  {
    label: "Would insert",
    meaning: "new records the import would create",
    value: r => r.stats.inserted ?? 0,
  },
  {
    label: "Would update",
    meaning: "existing records whose mapped fields would change",
    value: r => r.stats.updated ?? 0,
  },
  {
    label: "Skipped",
    meaning: "rows that match a record and change nothing",
    value: r => r.stats.skipped ?? 0,
  },
  {
    label: "Would flag deleted",
    meaning: "records the API no longer returns, which would be marked deleted",
    value: r => r.wouldFlag,
  },
  {
    label: "Would clear flags",
    meaning: "records marked deleted that the API returns again",
    value: r => r.wouldUnflag,
  },
  {
    label: "Would update parents",
    meaning: "parent record fields the import would write",
    value: r => r.wouldUpdateParents,
  },
  {
    label: "Unresolved",
    meaning: "changed rows whose parent record could not be found",
    value: r => r.unresolved,
  },
];

const PreviewResult: FC<{ result: VmetaImportDryRunResult }> = ({ result }) => (
  <div className="space-y-3">
    <table className="min-w-full text-sm">
      <tbody className="divide-y divide-gray-200">
        {PREVIEW_LINES.map(line => (
          <tr key={line.label} aria-label={line.label}>
            <th
              scope="row"
              className="py-1 pr-4 text-left font-medium text-gray-900">
              {line.label}
            </th>
            <td className="py-1 pr-4 font-mono">{line.value(result)}</td>
            <td className="py-1 text-gray-500">{line.meaning}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="text-sm">
      <p className="font-medium">Sample identifiers that would be inserted</p>
      <p className="font-mono text-xs">
        {result.sampleIdentifiers.insert.join(", ") || "None"}
      </p>
      <p className="mt-2 font-medium">
        Sample identifiers that would be updated
      </p>
      <p className="font-mono text-xs">
        {result.sampleIdentifiers.update.join(", ") || "None"}
      </p>
    </div>
  </div>
);

const PreviewPanel: FC<{ canvasId: string; configId: string }> = ({
  canvasId,
  configId,
}) => {
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const { mutateAsync: start, isPending } = useStartImportPreview();
  const { data: state, isError } = useImportPreview(
    canvasId,
    configId,
    previewId
  );
  const running =
    !!previewId &&
    !isError &&
    (state === undefined || state.status === "running");

  const onStart = async () => {
    setStartError(null);
    try {
      const { previewId: id } = await start({ canvasId, id: configId });
      setPreviewId(id);
    } catch (err) {
      setPreviewId(null);
      setStartError(errorMessage(err, "The preview could not start."));
    }
  };

  return (
    <section
      aria-labelledby="import-preview-heading"
      className="space-y-3 border-t border-gray-200 pt-6">
      <h3
        id="import-preview-heading"
        className="text-base font-semibold text-gray-900">
        Preview import
      </h3>
      <p className="text-sm text-gray-500">
        Runs the whole import with the saved settings and writes nothing. It can
        take a few minutes; you can keep working.
      </p>
      <button
        type="button"
        disabled={running || isPending}
        onClick={onStart}
        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
        Preview import
      </button>
      {startError && (
        <p role="alert" className="text-sm text-red-700">
          {startError}
        </p>
      )}
      {running && (
        <p role="status" className="text-sm text-gray-700">
          Preview running. This can take a few minutes.
        </p>
      )}
      {previewId && isError && (
        <p role="alert" className="text-sm text-red-700">
          Preview lost, start again.
        </p>
      )}
      {state?.status === "failed" && (
        <p role="alert" className="text-sm text-red-700">
          Preview failed: {state.reason}
        </p>
      )}
      {state?.status === "done" && <PreviewResult result={state.result} />}
    </section>
  );
};

export default PreviewPanel;
